/**
 * Formatters — the ONLY place BuildIntel converts a raw number/date into
 * a user-visible string. Consumers must never inline `toFixed(2)`,
 * `toLocaleString`, or hand-rolled `%` maths.
 *
 * Locale is intentionally pinned to `en-AU`; the app is Australia-only for
 * now and pretending otherwise would leak inconsistent commas into PDFs.
 */

import type { Money, Percent } from "@/types/models";

const AUD = new Intl.NumberFormat("en-AU", {
  style: "currency",
  currency: "AUD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const AUD_ROUND = new Intl.NumberFormat("en-AU", {
  style: "currency",
  currency: "AUD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

/** `$1,234.50` — every dollar figure the tradie sees goes through this. */
export const formatMoney = (value: Money): string => AUD.format(value);

/** `$1,235` — rounded, for tight KPI cards where cents are noise. */
export const formatMoneyRounded = (value: Money): string =>
  AUD_ROUND.format(value);

/** `62%` — integer percent. Use `formatPercent1(v)` if a decimal is needed. */
export const formatPercent = (value: Percent): string =>
  `${Math.round(value)}%`;

/** `62.5%` — one decimal, for cost-alert KPIs. */
export const formatPercent1 = (value: Percent): string =>
  `${value.toFixed(1).replace(/\.0$/, "")}%`;

/**
 * `21 Feb 2026` — human-friendly ISO-date renderer. Timezone-naive on
 * purpose: financial rows carry a date-only meaning; we don't want tz
 * drift shifting BAS quarters.
 */
export const formatDate = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-AU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/**
 * `20/07/2026` — compact AU numeric date. Use in tables and tight rows
 * where the "20 Jul 2026" long form would eat too much width. Non-locale
 * `Intl` call because Safari renders en-AU with unicode LRM marks in some
 * versions — the manual join guarantees plain ASCII slashes.
 */
export const formatDateShort = (iso: string): string => {
  if (!iso) return "";
  // Prefer parsing the leading YYYY-MM-DD substring so we don't inherit
  // Date's local-timezone shift for stored date-only values.
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${d.getFullYear()}`;
};

/** `21 Feb 2026 · 4:03 pm` — timestamp version, for audit logs. */
export const formatDateTime = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${formatDate(iso)} · ${d.toLocaleTimeString("en-AU", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })}`;
};
